/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RequestStatus } from "~/constants";
import {
  check2FASession,
  getAccountInfo,
  initAuthAsync,
  loginAsync,
  logoutAsync,
  send2FAEmail,
  validate2FAToken,
} from "~/features/auth/thunks";
import { AuthState, TwoFactorAuthState } from "~/features/auth/types";

const twoFactorAuthInitialState: TwoFactorAuthState = {
  tokenValidationStatus: RequestStatus.IDLE,
  sessionCheckStatus: RequestStatus.IDLE,
  emailSendingStatus: RequestStatus.IDLE,
  emailSentTo: null,
  required: false,
  differentSession: false,
};

const initialState: AuthState = {
  authInitialized: false,
  loginStatus: RequestStatus.IDLE,
  account: null,
  viewOnly: false,
  twoFactorAuth: twoFactorAuthInitialState,
};

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setNeed2FA: (state) => {
      state.twoFactorAuth.required = true;
    },
    reset2FAState: (state) => {
      state.twoFactorAuth = twoFactorAuthInitialState;
    },
    resetTokenValidationStatus: (state) => {
      state.twoFactorAuth.tokenValidationStatus = RequestStatus.IDLE;
    },
    setTokenValidationStatus: (state, action: PayloadAction<RequestStatus>) => {
      state.twoFactorAuth.tokenValidationStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      // Init
      .addCase(initAuthAsync.pending, (state) => {
        state.loginStatus = RequestStatus.PENDING;
      })

      .addCase(initAuthAsync.fulfilled, (state, action) => {
        state.loginStatus = RequestStatus.SUCCESS;
        state.account = action.payload.account;
        state.viewOnly = action.payload.viewOnly;
        state.authInitialized = true;
        state.twoFactorAuth.required = action.payload.twoFactorAuthRequired;
      })

      .addCase(initAuthAsync.rejected, (state) => {
        state.loginStatus = RequestStatus.IDLE; // Init is rejected when there's no session
        state.account = null;
        state.authInitialized = true;
      })

      // Login
      .addCase(loginAsync.pending, (state) => {
        state.loginStatus = RequestStatus.PENDING;
      })

      .addCase(loginAsync.rejected, (state) => {
        state.loginStatus = RequestStatus.ERROR;
        state.account = null;
      })

      .addCase(getAccountInfo.fulfilled, (state, action) => {
        state.loginStatus = RequestStatus.SUCCESS;
        state.account = action.payload.account;
        state.viewOnly = action.payload.viewOnly;
        state.twoFactorAuth = { ...twoFactorAuthInitialState, required: action.payload.twoFactorAuthRequired };
        state.account.config = action.payload.account.config;
      })

      .addCase(getAccountInfo.rejected, (state) => {
        state.loginStatus = RequestStatus.ERROR;
        state.account = null;
      })

      // Logout
      .addCase(logoutAsync.pending, (state) => {
        state.account = null;
        state.twoFactorAuth = twoFactorAuthInitialState;
      })

      .addCase(logoutAsync.fulfilled, (state) => {
        state.loginStatus = RequestStatus.IDLE;
      })

      .addCase(logoutAsync.rejected, (state) => {
        state.loginStatus = RequestStatus.ERROR;
      })

      // Validate 2FA token
      .addCase(validate2FAToken.pending, (state) => {
        state.twoFactorAuth.tokenValidationStatus = RequestStatus.PENDING;
        state.twoFactorAuth.required = false;
      })
      .addCase(validate2FAToken.fulfilled, (state) => {
        state.twoFactorAuth.tokenValidationStatus = RequestStatus.SUCCESS;
        state.twoFactorAuth.required = false;
      })
      .addCase(validate2FAToken.rejected, (state, action) => {
        state.twoFactorAuth.differentSession = action.payload === "Different session.";
        state.twoFactorAuth.tokenValidationStatus = RequestStatus.ERROR;
        state.twoFactorAuth.required = true;
      })
      // send email
      .addCase(send2FAEmail.pending, (state) => {
        state.twoFactorAuth.emailSendingStatus = RequestStatus.PENDING;
      })
      .addCase(send2FAEmail.fulfilled, (state, payload) => {
        state.twoFactorAuth.emailSendingStatus = RequestStatus.SUCCESS;
        state.twoFactorAuth.emailSentTo = payload.payload;
      })
      .addCase(send2FAEmail.rejected, (state) => {
        state.twoFactorAuth.emailSendingStatus = RequestStatus.ERROR;
      })
      // check session
      .addCase(check2FASession.pending, (state) => {
        state.twoFactorAuth.required = false;
        state.twoFactorAuth.sessionCheckStatus = RequestStatus.PENDING;
      })
      .addCase(check2FASession.fulfilled, (state) => {
        state.twoFactorAuth.required = false;
        state.twoFactorAuth.sessionCheckStatus = RequestStatus.SUCCESS;
      })
      .addCase(check2FASession.rejected, (state) => {
        state.twoFactorAuth.required = true;
        state.twoFactorAuth.sessionCheckStatus = RequestStatus.ERROR;
      });
  },
});

export const AuthActions = slice.actions;

export const authReducer = slice.reducer;
